import { OpenSearchRequestFilter } from '@libs/shared/domain/aggregations/opensearch/open-search-query.schema';
import { DateRange } from '@shared/components/chitin/molecules/daterange-picker/date-range';
import { AppliedFilter, FilterDef, FilterQueryInput } from '@shared/dataview/core/dataview-types';
import { isArray, isBoolean, isPlainObject, isString } from 'lodash';
import moment from 'moment-timezone';

const GROUP_BY_PREFIX = 'groupBy:';
const DATE_FORMAT = 'YYYY-MM-DD';
const DATETIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

function isNotEmpty(value: unknown): boolean {
  return value !== '' && value !== null && value !== undefined;
}

function isEmptyRange(input: FilterQueryInput): boolean {
  return input.type === 'range' && isPlainObject(input.value) && !Object.values(input.value as object).some(isNotEmpty);
}

export function toQueryInputs(applied: AppliedFilter[], defs: ReadonlyMap<string, FilterDef>): FilterQueryInput[] {
  const inputs: FilterQueryInput[] = [];

  for (const filter of applied) {
    const def = defs.get(filter.id);
    if (!def) continue;

    if (def.toQuery) {
      inputs.push(...def.toQuery({ def, applied: filter }));
      continue;
    }

    inputs.push({
      field: def.field,
      type: filter.type,
      value: filter.value,
      timezone: filter.timezone ?? def.query?.timezone,
      should: def.query?.should,
      groupBy: def.query?.groupBy,
      nested: def.query?.nested,
      caseInsensitive: def.caseInsensitive,
    });
  }

  return inputs;
}

export function buildRequestFilters(inputs: FilterQueryInput[]): OpenSearchRequestFilter[] {
  const filters: OpenSearchRequestFilter[] = [];
  const nestedByPath = new Map<string, OpenSearchRequestFilter>();

  for (const input of inputs) {
    if (isArray(input.value) ? input.value.length === 0 : !isNotEmpty(input.value)) continue;
    if (isEmptyRange(input)) continue;

    const group = extractGroupBy(input.value, input.groupBy);
    const value = input.transform ? input.transform(group.value) : group.value;

    const clause = mapOperator({ ...input, value });
    if (input.should) clause.should = true;
    if (group.groupBy) clause.groupBy = group.groupBy;

    if (input.nested) {
      const parent = nestedByPath.get(input.nested) ?? {
        operator: 'in' as const,
        negate: input.negate ?? false,
        field: input.nested,
        value: [],
        nested: [],
      };
      parent.nested?.push(clause);
      nestedByPath.set(input.nested, parent);
    } else {
      filters.push(clause);
    }
  }

  filters.push(...nestedByPath.values());

  return filters;
}

export function extractGroupBy(input: unknown, groupByField?: string): { groupBy?: string; value: unknown } {
  if (!isArray(input)) return { value: input };

  const grouped: string[] = [];
  let groupBy: string | undefined;

  for (const item of input) {
    if (isString(item) && item.startsWith(GROUP_BY_PREFIX)) {
      groupBy = groupByField;
      grouped.push(item.substring(GROUP_BY_PREFIX.length).replace('|', '#'));
    }
  }

  return { groupBy, value: grouped.length ? grouped : input };
}

export function mapOperator(
  input: Pick<FilterQueryInput, 'field' | 'type' | 'value' | 'timezone' | 'transform' | 'caseInsensitive'>,
): OpenSearchRequestFilter {
  const { field, type, value, timezone, transform, caseInsensitive } = input;

  switch (type) {
    case 'equal':
      return { operator: 'equal', field, value };
    case 'nequal':
      return { operator: 'equal', field, value, negate: true };
    case 'dateRange':
    case 'datetimeRange': {
      if (transform) return value as OpenSearchRequestFilter;

      const range = value as DateRange;
      let from = moment(range.from);
      let to = moment(range.to);
      from = timezone ? from.tz(timezone, true) : from;
      to = timezone ? to.tz(timezone, true) : to;

      return {
        operator: 'range',
        field,
        value: { [field]: { gte: from.format(DATETIME_FORMAT), lte: to.format(DATETIME_FORMAT) } },
      };
    }
    case 'date': {
      if (transform) return value as OpenSearchRequestFilter;
      return { operator: 'equal', field, value: moment(`${value}`).format(DATE_FORMAT) };
    }
    case 'in':
      if (isBoolean(value)) return { operator: 'equal', field, value };
      return { operator: 'in', field, value: isArray(value) ? value : [value], ...(caseInsensitive && { caseInsensitive }) };
    case 'nin':
      if (isBoolean(value)) return { operator: 'equal', field, value, negate: true };
      return { operator: 'in', field, value: isArray(value) ? value : [value], negate: true, ...(caseInsensitive && { caseInsensitive }) };
    case 'isOneOf':
      return { operator: 'in', field, value };
    case 'nisOneOf':
      return { operator: 'in', field, value, negate: true };
    case 'contains':
      return { operator: 'contains', field, value };
    case 'ncontains':
      return { operator: 'contains', field, value, negate: true };
    case 'beginsWith':
      return { operator: 'startsWith', field, value };
    case 'endsWith':
      return { operator: 'endsWith', field, value };
    case 'range':
      return { operator: 'range', field, value: { [field]: value } };
    case 'lt':
      return { operator: 'range', field, value: { [field]: { lt: value } } };
    case 'gt':
      return { operator: 'range', field, value: { [field]: { gt: value } } };
    case 'exists':
      return { operator: 'exists', field, value };
    case 'nexists':
      return { operator: 'exists', field, value, negate: true };
    default:
      throw new Error(`Unknown filter operator: ${type}`);
  }
}
